import { TenantFaqItem, TenantFaqText, TenantFaqs } from '../../api/account/accountApiTypes';

/**
 * The rules of a tenant FAQ question, checked here so the form can say what is
 * wrong before it saves. The server enforces them:
 * `new-backend/admin/src/lib/tenantFaqs.ts` (`parseFaqInput`, the limits) —
 * the admin-ui cannot import it, so the two name each other. Change one,
 * change both.
 */
export const MAX_FAQ_QUESTION_LENGTH = 200;
export const MAX_FAQ_ANSWER_LENGTH = 2000;
export const MAX_FAQS_PER_SECTION = 20;
export const MAX_FAQS_TOTAL = 60;

/** The form's four fields, as typed. */
export interface FaqDraft {
    questionDe: string;
    answerDe: string;
    questionEn: string;
    answerEn: string;
}

export const draftFrom = (item?: TenantFaqItem): FaqDraft => ({
    questionDe: item?.question.de ?? '',
    answerDe: item?.answer.de ?? '',
    questionEn: item?.question.en ?? '',
    answerEn: item?.answer.en ?? '',
});

/** Same normalisation as the server's `normaliseText`: CRLF → LF, trailing
 *  spaces per line dropped, at most one empty line in a row, ends trimmed. */
const textOf = (value: string): string | null => {
    const text = value
        .replace(/\r\n?/g, '\n')
        .split('\n')
        .map((line) => line.trimEnd())
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    return text || null;
};

/** What the save sends: blank fields become `null`. */
export const faqTextsOf = (draft: FaqDraft): { question: TenantFaqText, answer: TenantFaqText } => ({
    question: { de: textOf(draft.questionDe), en: textOf(draft.questionEn) },
    answer: { de: textOf(draft.answerDe), en: textOf(draft.answerEn) },
});

/**
 * Why the draft cannot be saved, as the server's error code (rendered through
 * `api_errors.<code>`), or `null`. Same order as the server checks.
 */
export const faqDraftProblem = (draft: FaqDraft): string | null => {
    const { question, answer } = faqTextsOf(draft);
    for (const lang of ['de', 'en'] as const) {
        if ((question[lang]?.length ?? 0) > MAX_FAQ_QUESTION_LENGTH
            || (answer[lang]?.length ?? 0) > MAX_FAQ_ANSWER_LENGTH) {
            return 'account_faq_text_invalid';
        }
        if (!!question[lang] !== !!answer[lang]) return 'account_faq_language_incomplete';
    }
    if (!(question.de && answer.de) && !(question.en && answer.en)) return 'account_faq_no_language';
    return null;
};

export const totalFaqs = (faqs?: TenantFaqs): number =>
    (faqs?.general.length ?? 0)
    + Object.values(faqs?.categories ?? {}).reduce((sum, list) => sum + list.length, 0);
